/**
 * Role-based helper utilities
 * Centralizes role checks for consistent RBAC across frontend
 */

import {
  ADMIN_ROLES,
  EXECUTIVE_ROLES,
  formatRoleLabel as sharedFormatRoleLabel,
  isAdminRole as sharedIsAdminRole,
  isExecutiveRole as sharedIsExecutiveRole,
  isSuperAdminRole as sharedIsSuperAdminRole,
  SUPER_ADMIN_ROLES,
  UserRole,
} from '@hrms/shared';
import { getAllStoredRoles } from './roleStorage';

export {
  ADMIN_ROLES,
  EXECUTIVE_ROLES,
  SUPER_ADMIN_ROLES,
};

/**
 * Formats a role label using stored custom roles first, falling back to shared formatter
 */
export const formatRoleLabel = (role?: string | null): string => {
  if (!role) return 'Employee';
  if (typeof window !== 'undefined') {
    try {
      const stored = getAllStoredRoles().find(
        (r) => r.value.toLowerCase() === role.toLowerCase().trim()
      );
      if (stored) return stored.label;
    } catch {
      // ignore
    }
  }
  return sharedFormatRoleLabel(role);
};

/**
 * Checks if a role is an executive role (built-in executive or custom role marked as executive)
 */
export const isExecutiveRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  const knownExecs = ['ceo', 'cfo', 'director', 'coo', 'cto', 'cmm'];
  if (knownExecs.includes(normalized)) return true;
  if (sharedIsExecutiveRole(role) || sharedIsExecutiveRole(normalized)) return true;
  if (typeof window !== 'undefined') {
    try {
      const stored = getAllStoredRoles().find(
        (r) => r.value.toLowerCase() === normalized || r.label.toLowerCase() === role.toLowerCase().trim()
      );
      return stored?.category === 'executive';
    } catch {
      // ignore
    }
  }
  return false;
};

/**
 * Checks if a role is Super Admin or Executive level
 */
export const isSuperAdminRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (normalized === 'super_admin' || role === UserRole.SUPER_ADMIN) return true;
  return isExecutiveRole(role);
};

/**
 * Checks if a role has Admin privileges (Super Admin, Executive, or Admin)
 */
export const isAdminRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (normalized === 'admin' || role === UserRole.ADMIN) return true;
  return isSuperAdminRole(role);
};

/**
 * Check if a role can approve leaves
 * @param role - User role to check
 * @returns true if role can approve leaves
 */
export const canApproveLeaves = (role?: string): boolean => {
  if (!role) return false;
  return [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE].includes(role as UserRole);
};


export const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',

  '/users': 'User Management',
  '/groups': 'Group Management',
  '/settings/policy-management': 'Leave Policies',
  '/payroll/manage': 'Manage Payslips',

  '/tickets': 'Tickets',
  '/attendance/leaves': 'Leave Management',
  '/attendance/leaves/approvals': 'Leave Approvals',

  '/payroll/my-payslips': 'My Payslips',

  '/reports/leaves': 'Leave Reports',
  '/reports/consolidate': 'Consolidate Leave Reports',
};

